import BreadCrumb from "Components/Common/BreadCrumb";
import PagePlanHeader from "Components/Common/PagePlanHeader";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import BenefitsModal from "Components/Modals/BenefitsModal";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import BillingHistory from "Components/Settings/Subscription/BillingHistory";
import { getPurchases, PRODUCT_ID } from "helpers/purchases";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { formatDateToReadable } from "utils/formatDates";
import { ErrorToast } from "utils/toast";
import { createSelector } from "reselect";
import {
  selectBillingInvoiceState,
  clearDownloadedInvoice,
} from "slices/billings/reducer";
import { downloadInvoice, getBillings, getUserDetails } from "slices/thunks";
import Icon from "Components/Icons/Icon";
import FullScreenLoader from "Components/Common/FullScreenLoader";
import { setRevenueBillingInfo } from "slices/auth/reducer";

const Subscription = () => {
  const bilingInvoices = useAppSelector((s) => s.billing.bilingInvoices);
  const [isInfoModalShow, setIsInfoModalShow] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  // const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const { user, revenueBillingInfo } = useAppSelector((state) => state.auth);
  const isSubscribed = user?.is_subscription_active;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [showRenewModal, setShowRenewModal] = useState<boolean>(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] =
    useState<boolean>(false);
  const customerInfo = revenueBillingInfo?.info;

  const billingInvoiceSelector = createSelector(
    selectBillingInvoiceState,
    (state) => ({
      billingInvoices: state.bilingInvoices,
      isLoading: state.status.fetch,
      isDownloading: state.status.download,
      next_pageUrl: state.next_pageUrl,
      downLoadUrls: state.downloadingUrls,
      downLoadedInvoice: state.downloadedInvoice,
      pageHistory: state.pageHistory,
    }),
  );

  const {
    billingInvoices,
    isLoading,
    isDownloading,
    next_pageUrl,
    downLoadUrls,
    downLoadedInvoice,
    pageHistory,
  } = useAppSelector(billingInvoiceSelector);

  useEffect(() => {
    const getPurchaseInfo = async () => {
      if (!user?.company?.id) {
        return;
      }
      try {
        setIsSubscriptionLoading(true);
        const purchases = getPurchases(user.company.id.toString());
        const info = await purchases.getCustomerInfo();
        console.log("Fetched Customer Info from Subscription Page:", info);

        dispatch(
          setRevenueBillingInfo({
            info: info,
            willRenew:
              info.subscriptionsByProductIdentifier[PRODUCT_ID]?.willRenew ||
              false,
          }),
        );
      } catch (error) {
        console.log("Error fetching purchase info:", error);
      } finally {
        setIsSubscriptionLoading(false);
      }
    };

    getPurchaseInfo();
  }, [dispatch, user?.company?.id]);

  useEffect(() => {
    dispatch(getUserDetails());
  }, []);

  const handleShowInfoModal = () => {
    setIsInfoModalShow((prev) => !prev);
  };

  const handleSubscription = () => {
    navigate("/subscribe-plan");
  };

  const onCancelSubscription = () => {
    if (!user?.company?.id) {
      return;
    }

    setShowRenewModal(true);

    // if (revenueBillingInfo?.willRenew) {
    //   setShowCancelModal(true);
    // } else {
    //   setShowRenewModal(true);
    // }
  };

  const handleConfirmCancel = async () => {
    if (!user?.company?.id) {
      return;
    }

    // if (showCancelModal) {
    //   setShowCancelModal(false);
    // } else {
    setShowRenewModal(false);
    // }
    setIsCancelling(true);
    try {
      const sub = customerInfo?.subscriptionsByProductIdentifier[PRODUCT_ID];

      console.log("Parent Store mismatch:", sub?.store);
      if (sub?.store !== "rc_billing" && sub?.store !== "stripe") {
        console.log("Store mismatch:", sub?.store);

        ErrorToast("Cannot manage subscription: Store mismatch");
        return;
      }

      console.log("Cancel Plan Pressed", customerInfo);

      // Open the management URL where user can cancel
      if (customerInfo?.managementURL) {
        window.open(customerInfo.managementURL, "_blank");
      } else {
        ErrorToast("Unable to open subscription management. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching subscription info:", error);
      ErrorToast("Failed to load subscription details. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDownload = (invoiceUrl: string) => {
    dispatch(downloadInvoice({ invoiceUrl }));
  };

  useEffect(() => {
    if (isDownloading === "succeeded" && downLoadedInvoice) {
      const a = document.createElement("a");
      a.href = downLoadedInvoice;
      a.download = "invoice.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downLoadedInvoice);
      // Clear the downloaded invoice from state to prevent auto-download on refresh
      dispatch(clearDownloadedInvoice());
    }
  }, [isDownloading, downLoadedInvoice, dispatch]);

  function getStartingAfter(url?: string | null): string | null {
    if (!url) return null;

    try {
      return new URL(url).searchParams.get("starting_after");
    } catch {
      return null; // invalid URL
    }
  }

  useEffect(() => {
    dispatch(
      getBillings({
        starting_after: getStartingAfter(pageHistory[currentPage - 1]),
      }),
    );
  }, [dispatch, currentPage]);

  const onTeamMemberClick = () => {
    ErrorToast(
      "Only the account owner can manage subscriptions. Please contact your owner.",
    );
  };

  document.title = "Billing History | EaziQuote";

  return (
    <div className="page-content">
      <FullScreenLoader
        isLoading={isSubscriptionLoading || isLoading === "loading"}
        message="Loading subscription details..."
      />
      <Container fluid>
        <BreadCrumb
          title="Subscription & Billing"
          pageTitle="Settings"
          url="/settings"
        />
        {!isSubscribed && user?.is_trial_period ? (
          <PagePlanHeader
            btnLabel="Subscribe"
            title="Free Plan"
            subtitle={`Free trial ends on ${formatDateToReadable(
              user?.subscription_ended_at || "",
            )}`}
            icon="Crown"
            onClick={() => {
              if (user?.is_team_member) {
                onTeamMemberClick();
              } else {
                handleSubscription();
              }
            }}
          />
        ) : (
          <div className="row g-4">
            <div className="col-12 ">
              <PagePlanHeader
                btnLabel={
                  !user?.is_subscription_active && bilingInvoices.length === 0
                    ? "Subscribe"
                    : "Manage Subscription & Billing"
                }
                title={!isSubscribed ? "No Active Plan" : "Pro Plan"}
                subtitle={
                  !isSubscribed
                    ? bilingInvoices.length === 0
                      ? "Get the Pro plan to enjoy all premium features."
                      : "Your subscription has expired."
                    : user?.subscription_amount || ""
                }
                icon="Crown"
                isInfo
                isDefaultBtn={!isSubscribed}
                onInfoClick={handleShowInfoModal}
                onClick={
                  user?.is_team_member
                    ? onTeamMemberClick
                    : user?.is_subscription_active
                    ? onCancelSubscription
                    : handleSubscription
                }
                disabled={isCancelling}
              />
            </div>

            {/* <div className="col-12 col-md-6  col-xl-6">
              <PagePlanHeader
                btnLabel="Change"
                title="Visa"
                subtitle="•••• •••• •••• 4069"
                tagline="Expires on 21 August 2028"
                icon="PaymentDetail"
                isDefaultBtn={false}
                onClick={handleShowPaymentModal}
              />
            </div> */}
          </div>
        )}
        <BillingHistory
          billingDetails={billingInvoices || []}
          handleDownload={handleDownload}
          downLoadUrls={downLoadUrls || []}
          isLoadingData={isLoading === "loading"}
          currentPage={currentPage}
          nextPage={next_pageUrl}
          handlePageChange={setCurrentPage}
        />
        {/* <CustomFormModal
          isOpen={isModalShow}
          onCancel={handleShowPaymentModal}
          title="Update Payment Details"
          fields={paymentFields}
          submitText="Save Details"
          note="By proceeding, your payment method will be securely updated for future transactions."
          validation={validation}
        /> */}
        <BenefitsModal
          isOpen={isInfoModalShow}
          onCancel={handleShowInfoModal}
        />
        {/* <DeleteItemModal
          show={showCancelModal}
          onCloseClick={() => setShowCancelModal(false)}
          onDeleteClick={handleConfirmCancel}
          title="Cancel Subscription"
          description="Are you sure you want to cancel your subscription?"
          confirmText="Yes, Cancel"
          icon={<i className="ri-close-circle-line display-5 text-danger"></i>}
        /> */}
        <DeleteItemModal
          show={showRenewModal}
          onCloseClick={() => setShowRenewModal(false)}
          onDeleteClick={handleConfirmCancel}
          title="Manage Subscription & Billing"
          description="Do you want to manage your subscription and billing details?"
          confirmText="Confirm"
          icon={<Icon name="SubscriptionRequired" fontSize={80} />}
        />
      </Container>
    </div>
  );
};

export default Subscription;
